import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, ArrowLeft, Loader2, Trash2, QrCode, ExternalLink, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_LIMITS = {
  hero_image: { maxMB: 10, types: '.jpg,.jpeg,.png,.webp', label: 'JPG, PNG, WebP (max 10MB)' },
  welcome_video: { maxMB: 500, types: 'video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm', label: 'MP4, MOV, WebM (max 500MB)' },
  story_video: { maxMB: 500, types: 'video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm', label: 'MP4, MOV, WebM (max 500MB)' },
  audio_file: { maxMB: 100, types: 'audio/mpeg,audio/wav,audio/mp4,audio/aac,.mp3,.wav,.m4a,.aac', label: 'MP3, WAV, M4A (max 100MB)' },
  pdf_book: { maxMB: 50, types: 'application/pdf,.pdf', label: 'PDF only (max 50MB)' },
  photos: { maxMB: 10, types: '.jpg,.jpeg,.png,.webp', label: 'JPG, PNG, WebP (max 10MB each)' },
};

function FileUploadZone({ label, accept, mediaType, projectId, currentFile, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const limits = FILE_LIMITS[mediaType];

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { Authorization: `Bearer ${token}` };
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (limits && file.size > limits.maxMB * 1024 * 1024) {
      toast.error(`File too large. Maximum: ${limits.maxMB}MB`);
      e.target.value = '';
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/upload/${mediaType}`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      );
      onUploaded(data);
      toast.success(`${label} uploaded successfully`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : `Failed to upload ${label}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="border border-dashed border-[#D6D3CD] p-4 sm:p-5" data-testid={`upload-zone-${mediaType}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-widest font-semibold text-[#78716C]">{label}</p>
        {currentFile && (
          <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5">Uploaded</span>
        )}
      </div>
      {currentFile ? (
        <div className="flex items-center gap-3">
          <p className="text-sm text-[#57534E] truncate flex-1">{currentFile.original_filename}</p>
          <label className="text-xs text-[#1C1917] border border-[#D6D3CD] hover:bg-[#F2EFE9] px-3 py-1.5 cursor-pointer transition-colors">
            Replace
            <input type="file" accept={accept} onChange={handleUpload} className="hidden" />
          </label>
        </div>
      ) : (
        <label className={`flex flex-col items-center justify-center py-6 cursor-pointer text-[#78716C] hover:text-[#57534E] transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
          {uploading ? (
            <Loader2 size={24} className="animate-spin mb-2" />
          ) : (
            <Upload size={24} className="mb-2" />
          )}
          <span className="text-sm">{uploading ? 'Uploading...' : `Upload ${label}`}</span>
          {limits && <span className="text-[10px] text-[#A89F91] mt-1">{limits.label}</span>}
          <input type="file" accept={accept} onChange={handleUpload} className="hidden" disabled={uploading} />
        </label>
      )}
    </div>
  );
}

export default function ProjectEditor() {
  const { projectId } = useParams();
  const isNew = !projectId || projectId === 'new';
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [photosUploading, setPhotosUploading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [form, setForm] = useState({
    loved_one_name: '',
    slug: '',
    tagline: 'This is their story. Keep it close.',
    access_type: 'public',
    passcode: '',
    is_published: false,
    life_lessons: [],
  });
  const [project, setProject] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate('/admin/login');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!isNew && user) fetchProject();
  }, [isNew, user]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };
  };

  const fetchProject = async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}`, getAuthHeaders());
      setProject(data);
      setForm({
        loved_one_name: data.loved_one_name || '',
        slug: data.slug || '',
        tagline: data.tagline || '',
        access_type: data.access_type || 'public',
        passcode: data.passcode || '',
        is_published: data.is_published || false,
        life_lessons: data.life_lessons || [],
      });
    } catch {
      toast.error('Failed to load project');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleSave = async () => {
    if (!form.loved_one_name || !form.slug) {
      toast.error('Name and slug are required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await axios.post(`${API}/projects`, form, getAuthHeaders());
        toast.success('Project created!');
        navigate(`/admin/projects/${data.id}`);
      } else {
        await axios.put(`${API}/projects/${projectId}`, form, getAuthHeaders());
        toast.success('Project updated!');
        fetchProject();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotosUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const maxSize = FILE_LIMITS.photos.maxMB * 1024 * 1024;
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > maxSize) {
        toast.error(`${files[i].name} is too large. Maximum: ${FILE_LIMITS.photos.maxMB}MB`);
        return;
      }
    }
    setPhotosUploading(true);
    for (let i = 0; i < files.length; i++) {
      const formData = new FormData();
      formData.append('file', files[i]);
      try {
        const { data } = await axios.post(
          `${API}/projects/${project.id}/upload/photos`,
          formData,
          { headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }, withCredentials: true }
        );
        setProject(data);
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : `Failed to upload ${files[i].name}`);
      }
    }
    setPhotosUploading(false);
    toast.success('Photos uploaded');
  };

  const removePhoto = async (index) => {
    try {
      await axios.delete(`${API}/projects/${project.id}/photos/${index}`, getAuthHeaders());
      fetchProject();
      toast.success('Photo removed');
    } catch {
      toast.error('Failed to remove photo');
    }
  };

  const addLesson = () => setForm({ ...form, life_lessons: [...form.life_lessons, { text: '', attribution: '' }] });
  const updateLesson = (i, field, value) => {
    const updated = [...form.life_lessons];
    updated[i] = { ...updated[i], [field]: value };
    setForm({ ...form, life_lessons: updated });
  };
  const removeLesson = (i) => setForm({ ...form, life_lessons: form.life_lessons.filter((_, idx) => idx !== i) });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#A89F91] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="project-editor">
      <header className="border-b border-[#E7E5DF] bg-[#FCFBF9]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 flex items-center justify-between h-16">
          <Link to="/admin" className="flex items-center gap-2 text-sm text-[#78716C] hover:text-[#1C1917] transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft size={16} /> Back to Dashboard
          </Link>
          <div className="flex items-center gap-3">
            {!isNew && project && (
              <>
                <button onClick={() => setShowQR(true)} data-testid="editor-qr-button" className="p-2 text-[#78716C] hover:text-[#1C1917] transition-colors" title="QR Code">
                  <QrCode size={18} />
                </button>
                <Link to={`/admin/preview/${project.id}`} target="_blank" data-testid="editor-preview-link" className="flex items-center gap-1.5 text-sm text-[#78716C] border border-[#D6D3CD] hover:bg-[#F2EFE9] px-3 py-1.5 transition-colors">
                  <ExternalLink size={14} /> Preview
                </Link>
              </>
            )}
            <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-project-button"
            className="bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] disabled:opacity-50 px-6 py-2 text-sm font-medium tracking-wide transition-all duration-300 flex items-center gap-2"
          >
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : isNew ? 'Create Project' : 'Save Changes'}
          </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 sm:px-12 py-8 sm:py-12">
        <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-medium text-[#1C1917] mb-8">
          {isNew ? 'New Heirloom Project' : `Editing: ${form.loved_one_name}`}
        </h2>

        {/* Basic Info */}
        <div className="space-y-6 mb-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-2">Loved One's Name *</label>
              <input
                value={form.loved_one_name}
                onChange={(e) => {
                  setForm({ ...form, loved_one_name: e.target.value, slug: isNew ? generateSlug(e.target.value) : form.slug });
                }}
                data-testid="project-name-input"
                className="w-full border border-[#D6D3CD] focus:border-[#1C1917] px-4 py-3 text-[#1C1917] outline-none transition-colors text-sm bg-transparent"
                placeholder="e.g., Margaret Johnson"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-2">URL Slug *</label>
              <input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: generateSlug(e.target.value) })}
                data-testid="project-slug-input"
                className="w-full border border-[#D6D3CD] focus:border-[#1C1917] px-4 py-3 text-[#1C1917] outline-none transition-colors text-sm bg-transparent"
                placeholder="e.g., margaret-johnson"
              />
              {form.slug && (
                <p className="text-xs text-[#A89F91] mt-1">/heirloom/{form.slug}</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-2">Tagline</label>
            <input
              value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              data-testid="project-tagline-input"
              className="w-full border border-[#D6D3CD] focus:border-[#1C1917] px-4 py-3 text-[#1C1917] outline-none transition-colors text-sm bg-transparent"
            />
          </div>
        </div>

        {/* Access Settings */}
        <div className="border-t border-[#E7E5DF] pt-8 mb-10">
          <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#1C1917] mb-6">Access Settings</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-2">Access Type</label>
              <select
                value={form.access_type}
                onChange={(e) => setForm({ ...form, access_type: e.target.value })}
                data-testid="project-access-type"
                className="w-full border border-[#D6D3CD] focus:border-[#1C1917] px-4 py-3 text-[#1C1917] outline-none transition-colors text-sm bg-transparent"
              >
                <option value="public">Public (anyone with the link)</option>
                <option value="passcode">Passcode Protected</option>
                <option value="magic_link">Magic Link</option>
              </select>
            </div>
            {form.access_type === 'passcode' && (
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-2">Passcode</label>
                <input
                  value={form.passcode}
                  onChange={(e) => setForm({ ...form, passcode: e.target.value })}
                  data-testid="project-passcode-input"
                  className="w-full border border-[#D6D3CD] focus:border-[#1C1917] px-4 py-3 text-[#1C1917] outline-none transition-colors text-sm bg-transparent"
                  placeholder="Enter a passcode"
                />
              </div>
            )}
            {form.access_type === 'magic_link' && project?.magic_link_token && (
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-2">Magic Link</label>
                <p className="text-sm text-[#57534E] break-all">{window.location.origin}/heirloom/magic/{project.magic_link_token}</p>
              </div>
            )}
          </div>
          <div className="mt-6 flex items-center gap-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                data-testid="project-published-checkbox"
                className="w-4 h-4 accent-[#1C1917]"
              />
              <span className="text-sm text-[#1C1917]">Published</span>
            </label>
          </div>
        </div>

        {/* Media Uploads - only after project is created */}
        {!isNew && project && (
          <div className="border-t border-[#E7E5DF] pt-8 mb-10">
            <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#1C1917] mb-6">Media</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FileUploadZone label="Hero Image" accept={FILE_LIMITS.hero_image.types} mediaType="hero_image" projectId={project.id} currentFile={project.hero_image} onUploaded={setProject} />
              <FileUploadZone label="Welcome Video" accept={FILE_LIMITS.welcome_video.types} mediaType="welcome_video" projectId={project.id} currentFile={project.welcome_video} onUploaded={setProject} />
              <FileUploadZone label="Story Video" accept={FILE_LIMITS.story_video.types} mediaType="story_video" projectId={project.id} currentFile={project.story_video} onUploaded={setProject} />
              <FileUploadZone label="Audio Recording" accept={FILE_LIMITS.audio_file.types} mediaType="audio_file" projectId={project.id} currentFile={project.audio_file} onUploaded={setProject} />
              <FileUploadZone label="Life Story Book (PDF)" accept={FILE_LIMITS.pdf_book.types} mediaType="pdf_book" projectId={project.id} currentFile={project.pdf_book} onUploaded={setProject} />
            </div>

            {/* Photos */}
            <div className="mt-6" data-testid="photos-upload-section">
              <p className="text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-3">Photo Gallery</p>
              <div className="border border-dashed border-[#D6D3CD] p-4">
                {project.photos && project.photos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-4">
                    {project.photos.map((photo, i) => (
                      <div key={i} className="relative aspect-square group">
                        <img src={`${API}/files/${photo.storage_path}`} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => removePhoto(i)}
                          data-testid={`remove-photo-${i}`}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <label className={`flex items-center justify-center gap-2 py-4 cursor-pointer text-[#78716C] hover:text-[#57534E] transition-colors ${photosUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  {photosUploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                  <span className="text-sm">{photosUploading ? 'Uploading...' : 'Add Photos'}</span>
                  <input type="file" accept={FILE_LIMITS.photos.types} multiple onChange={handlePhotosUpload} className="hidden" disabled={photosUploading} />
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Life Lessons */}
        <div className="border-t border-[#E7E5DF] pt-8 mb-10">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#1C1917]">Life Lessons</h3>
            <button
              onClick={addLesson}
              data-testid="add-lesson-button"
              className="text-sm text-[#1C1917] border border-[#D6D3CD] hover:bg-[#F2EFE9] px-4 py-1.5 transition-colors"
            >
              + Add Lesson
            </button>
          </div>
          <div className="space-y-4">
            {form.life_lessons.map((lesson, i) => (
              <div key={i} className="border border-[#E7E5DF] p-4 space-y-3" data-testid={`lesson-editor-${i}`}>
                <textarea
                  value={lesson.text}
                  onChange={(e) => updateLesson(i, 'text', e.target.value)}
                  placeholder="Quote or wisdom..."
                  className="w-full border border-[#D6D3CD] focus:border-[#1C1917] px-3 py-2 text-sm outline-none resize-none bg-transparent"
                  rows={2}
                />
                <div className="flex items-center gap-3">
                  <input
                    value={lesson.attribution}
                    onChange={(e) => updateLesson(i, 'attribution', e.target.value)}
                    placeholder="Attribution (optional)"
                    className="flex-1 border border-[#D6D3CD] focus:border-[#1C1917] px-3 py-2 text-sm outline-none bg-transparent"
                  />
                  <button onClick={() => removeLesson(i)} className="p-2 text-red-400 hover:text-red-600 transition-colors" data-testid={`remove-lesson-${i}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {isNew && (
          <p className="text-sm text-[#A89F91] italic">Save the project first, then you can upload media files.</p>
        )}
      </div>

      {/* QR Code Modal */}
      {showQR && project && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 animate-fade-in" onClick={() => setShowQR(false)} data-testid="editor-qr-modal">
          <div className="bg-[#FCFBF9] max-w-sm w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowQR(false)} className="absolute top-4 right-4 text-[#78716C] hover:text-[#1C1917]"><X size={20} /></button>
            <div className="text-center">
              <p className="overline mb-2">QR Code</p>
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#1C1917] mb-6">{project.loved_one_name}</h3>
              <div className="flex justify-center mb-6">
                <QRCodeSVG
                  value={`${window.location.origin}/heirloom/${project.slug}`}
                  size={220}
                  level="H"
                  fgColor="#1C1917"
                  bgColor="#FCFBF9"
                />
              </div>
              <p className="text-xs text-[#78716C] mb-4 break-all">{window.location.origin}/heirloom/{project.slug}</p>
              <button
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/heirloom/${project.slug}`); toast.success('Link copied!'); }}
                className="w-full flex items-center justify-center gap-2 border border-[#D6D3CD] hover:bg-[#F2EFE9] py-2.5 text-sm text-[#1C1917] transition-colors"
              >
                Copy Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
